import { reverse } from '/@/utils/router';

/**
 * URL for a "recommend a hybrid for this field" link.
 *
 * Corn goes to the v2 wizard, which takes the field as a query param and opens on the
 * environment step. Soy stays on the single-page editor until the wizard's density curve and
 * defensive profile — both corn-only data — support it. Keeping that decision here means the
 * eventual soy support is a one-line change rather than a hunt through every entry point.
 */
export const seedRecommendationUrl = ({
    crop,
    fieldId,
    entrySource,
    userNodeId,
    from,
    plantingDate,
    productProfile,
    climatePattern,
    soilMoisture,
}: {
    crop: string;
    /** Omit to start at field selection; both flows otherwise open on the chosen field. */
    fieldId?: number | string;
    entrySource?: string;
    userNodeId?: number | string;
    /** Marks the crop-monitoring handoff, which returns there once the recommendation saves. */
    from?: 'cropMonitoring';
    /** Prefilled crop configuration, as handed over by the density recommendation. */
    plantingDate?: string;
    productProfile?: number | string;
    climatePattern?: number | string;
    soilMoisture?: number | string;
}) => {
    // Snake_case keys are what both flows read back off the query string.
    const qs = {
        entrySource,
        userNodeId,
        from,
        planting_date: plantingDate,
        product_profile: productProfile,
        climate_pattern: climatePattern,
        soil_moisture: soilMoisture,
    };

    if (crop === 'corn') {
        return reverse('recommendations.RecommendationFieldSelect', {
            params: { crop },
            qs: { ...qs, fieldId },
        });
    }

    return reverse('recommendations.RecommendationEdit', { params: { crop, fieldId }, qs });
};

/**
 * URL for viewing a saved seed recommendation.
 *
 * Corn opens the v2 result page; soy keeps the original read-only view, since the v2 page is
 * built on corn-only data (density curve, phenology, defensive profile). Every entry point
 * routes through here so the eventual soy support is a one-line change — and so no caller has
 * to remember the gate.
 */
export const recommendationResultUrl = ({
    recommendationId,
    crop,
    qs,
}: {
    recommendationId: number | string;
    crop: string;
    qs?: Record<string, string | undefined>;
}) => {
    const params = { recommendationId, crop };

    if (crop === 'corn') {
        return reverse('recommendations.RecommendationResultV2', { params, qs });
    }

    return reverse('recommendations.RecommendationResult', { params, qs });
};
